import * as PopoverPrimitive from "@radix-ui/react-popover";
import { AnimatePresence, m } from "framer-motion";
import { Check, ChevronDown, X } from "lucide-react";
import { type KeyboardEvent, useId, useMemo, useRef, useState } from "react";
import { cn } from "../cn";
import { springSoft, useMotionTransition } from "../motion";
import type { ComboboxOption } from "./Combobox";
import { useSheetPortalContainer } from "./Sheet";

export interface MultiSelectProps {
  readonly values: readonly string[];
  readonly onValuesChange: (values: string[]) => void;
  readonly options: readonly ComboboxOption[];
  /**
   * Applied to the trigger button, so a `Field`'s `<label htmlFor>` points at
   * the element the user actually operates.
   */
  readonly id?: string;
  readonly "aria-label": string;
  readonly "aria-describedby"?: string;
  readonly "aria-invalid"?: boolean;
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly emptyText?: string;
  readonly loading?: boolean;
  readonly size?: "sm" | "md";
  readonly className?: string;
  /**
   * Called with the raw search text on every keystroke, for server-driven
   * option lists. The component still applies its own client-side
   * `label.includes(query)` filter on top, so a stale response with extra rows
   * is harmless. See the matching note on {@link Combobox}.
   */
  readonly onQueryChange?: (query: string) => void;
  /**
   * When `true`, the control fills its container width so it lines up with
   * sibling form fields. Defaults to `false` (intrinsic width with a floor),
   * which suits compact toolbar/filter rows.
   */
  readonly fluid?: boolean;
}

/**
 * Searchable multi-select (WAI-ARIA listbox with `aria-multiselectable`).
 * Mirrors {@link Combobox}'s interaction model — focus stays on the search
 * input, options are tracked via `aria-activedescendant`, and selecting an
 * option toggles membership without closing the popover so several picks can be
 * made in one pass. Chosen options render as removable chips in the trigger.
 *
 * `aria-required` is deliberately not supported: no element here carries a
 * role that supports it (the trigger is a plain button). Supporting it means
 * promoting the trigger to `role="combobox"`, which changes what screen
 * readers announce and needs its own testing — do not re-add the prop.
 */
export function MultiSelect({
  values,
  onValuesChange,
  options,
  id,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  placeholder = "Selecteer...",
  searchPlaceholder = "Zoeken...",
  emptyText = "Geen resultaten",
  loading = false,
  size = "md",
  className,
  onQueryChange,
  fluid = false,
}: MultiSelectProps) {
  const popoverTransition = useMotionTransition(springSoft);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef(new Map<number, HTMLButtonElement>());
  const listboxId = useId();
  const sheetContainer = useSheetPortalContainer();

  // Chips follow the caller's `values` order so removing and re-adding an
  // option puts it back at the end predictably.
  const selectedOptions = useMemo(
    () =>
      values.flatMap((value) => {
        const option = options.find((candidate) => candidate.value === value);
        return option ? [option] : [];
      }),
    [options, values],
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedQuery),
    );
  }, [options, query]);

  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const scrollOptionIntoView = (index: number) => {
    optionRefs.current.get(index)?.scrollIntoView({ block: "nearest" });
  };

  const toggleValue = (value: string) => {
    onValuesChange(
      values.includes(value)
        ? values.filter((current) => current !== value)
        : [...values, value],
    );
  };

  const removeValue = (
    value: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    onValuesChange(values.filter((current) => current !== value));
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery("");
    } else {
      setActiveIndex(0);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (
        event.key === "ArrowDown" ||
        event.key === "ArrowUp" ||
        event.key === "Enter"
      ) {
        event.preventDefault();
        handleOpenChange(true);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        setActiveIndex((current) => {
          const next =
            filteredOptions.length === 0
              ? 0
              : (current + 1) % filteredOptions.length;
          scrollOptionIntoView(next);
          return next;
        });
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        setActiveIndex((current) => {
          const next =
            filteredOptions.length === 0
              ? 0
              : (current - 1 + filteredOptions.length) % filteredOptions.length;
          scrollOptionIntoView(next);
          return next;
        });
        break;
      }
      case "Home": {
        event.preventDefault();
        setActiveIndex(0);
        scrollOptionIntoView(0);
        break;
      }
      case "End": {
        event.preventDefault();
        const last = Math.max(0, filteredOptions.length - 1);
        setActiveIndex(last);
        scrollOptionIntoView(last);
        break;
      }
      case "Enter": {
        event.preventDefault();
        const option = filteredOptions[activeIndex];
        if (option) {
          toggleValue(option.value);
        }
        break;
      }
      case "Backspace": {
        // Quick removal of the last chip when the search box is empty, matching
        // the familiar tag-input convention.
        if (query === "" && values.length > 0) {
          event.preventDefault();
          onValuesChange(values.slice(0, -1));
        }
        break;
      }
      case "Escape": {
        event.preventDefault();
        handleOpenChange(false);
        break;
      }
      default:
        break;
    }
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Anchor asChild>
        <div
          className={cn(
            "relative flex items-center rounded-xl border border-input bg-surface py-1.5 pl-3 pr-8 text-sm text-foreground shadow-sm transition-colors hover:bg-muted/50 has-[:focus-visible]:border-primary has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/15 has-[[aria-invalid=true]]:border-destructive",
            fluid ? "w-full" : "min-w-40",
            size === "sm" ? "min-h-9" : "min-h-10",
            loading && "pointer-events-none opacity-50",
            className,
          )}
        >
          {/* Full-bleed trigger so the whole control opens the listbox — the
              chips render on top with pointer-events disabled, so a click on any
              empty space (or a chip body) still reaches this button and lets a
              user keep adding options after the first pick. Only the chip remove
              buttons re-enable pointer events to intercept their own clicks. */}
          <PopoverPrimitive.Trigger asChild>
            <button
              type="button"
              id={id}
              disabled={loading}
              aria-label={ariaLabel}
              aria-describedby={ariaDescribedBy}
              aria-invalid={ariaInvalid}
              aria-haspopup="listbox"
              aria-expanded={open}
              className="absolute inset-0 h-full w-full rounded-xl focus-visible:outline-none"
            >
              <span className="sr-only">{ariaLabel}</span>
            </button>
          </PopoverPrimitive.Trigger>
          <div className="pointer-events-none relative flex w-full flex-wrap items-center gap-1.5">
            {selectedOptions.length === 0 && (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            {selectedOptions.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 rounded-md bg-primary-soft py-0.5 pl-2 pr-1 text-xs font-medium text-primary"
              >
                {option.label}
                <button
                  type="button"
                  aria-label={`${option.label} verwijderen`}
                  onClick={(event) => removeValue(option.value, event)}
                  className="pointer-events-auto grid size-4 place-items-center rounded-full text-primary/70 transition-colors hover:bg-primary/15 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
          <ChevronDown
            className="pointer-events-none absolute inset-y-0 right-3 my-auto size-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </div>
      </PopoverPrimitive.Anchor>
      <AnimatePresence>
        {open && (
          <PopoverPrimitive.Portal
            forceMount
            container={sheetContainer ?? undefined}
          >
            <PopoverPrimitive.Content
              asChild
              forceMount
              align="start"
              sideOffset={4}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                searchInputRef.current?.focus();
              }}
            >
              <m.div
                initial={{ opacity: 0, scale: 0.96, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: 2 }}
                transition={popoverTransition}
                className="z-50 w-[var(--radix-popover-trigger-width)] min-w-56 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg focus:outline-none"
              >
                <div className="border-b border-border/60 p-1.5">
                  <input
                    ref={searchInputRef}
                    type="text"
                    role="combobox"
                    aria-label={ariaLabel}
                    aria-expanded={open}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    aria-activedescendant={
                      filteredOptions.length > 0
                        ? optionId(activeIndex)
                        : undefined
                    }
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setActiveIndex(0);
                      onQueryChange?.(event.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={searchPlaceholder}
                    className="w-full rounded-lg bg-transparent px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                </div>
                <div
                  id={listboxId}
                  role="listbox"
                  aria-label={ariaLabel}
                  aria-multiselectable="true"
                  className="max-h-64 overflow-y-auto p-1"
                >
                  {filteredOptions.length === 0 && (
                    <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                      {emptyText}
                    </p>
                  )}
                  {filteredOptions.map((option, index) => {
                    const isSelected = values.includes(option.value);
                    const isActive = index === activeIndex;
                    return (
                      <button
                        key={option.value}
                        ref={(node) => {
                          if (node) {
                            optionRefs.current.set(index, node);
                          } else {
                            optionRefs.current.delete(index);
                          }
                        }}
                        type="button"
                        id={optionId(index)}
                        role="option"
                        tabIndex={-1}
                        aria-selected={isSelected}
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => toggleValue(option.value)}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors",
                          isActive && "bg-primary-soft text-primary",
                          !isActive && "text-foreground hover:bg-muted/50",
                        )}
                      >
                        <span className="truncate">
                          {option.label}
                          {option.hint && (
                            <span className="ml-1.5 text-muted-foreground">
                              {option.hint}
                            </span>
                          )}
                        </span>
                        {isSelected && (
                          <Check
                            className="size-4 shrink-0"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </m.div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        )}
      </AnimatePresence>
    </PopoverPrimitive.Root>
  );
}
